/**
 * The Waslo AI section's route table — THE PLAN, §2.
 *
 * TWO DELIBERATE OMISSIONS, and they are the whole point of this file:
 *
 *   • NO `featureFlag`. `usePolicy.js:28-31` returns true for a falsy flag, so leaving it
 *     unset needs no plumbing and no premium-feature state.
 *   • NO `installationTypes`. This is exactly why Captain is invisible on this community
 *     self-host: `captain.routes.js:20-24` declares `[CLOUD, ENTERPRISE]`, and
 *     `usePolicy.js:39-51` `checkInstallationType` returns false for a COMMUNITY install,
 *     so `SidebarGroup` filters every Captain child out and the group never renders —
 *     while the routes themselves still resolve, because the router guard reads
 *     `meta.permissions` ONLY (`helper/routeHelpers.js:15-18`). We will not repeat that.
 *
 * `meta.permissions: ['administrator']` is therefore the single gate, and it is enforced
 * in both places at once: the router guard uses it to admit the navigation, and
 * `provider.js:105-113` `resolvePermissions` reads it back off the target route to decide
 * whether the sidebar entry renders. One declaration, two effects, no drift.
 *
 * WHY THE TABLE IS COMPLETE WHILE THE PAGES ARE NOT: `SidebarGroup` resolves every
 * child's `to` through `router.resolve()`, which throws on an unregistered name and takes
 * the primary nav down with it. Unbuilt destinations therefore pointed at
 * `WasloNotReadyPage` and were replaced page by page — the component swaps, the name, the
 * path and the nav entry never move. **There are none left**: the last two ("still being
 * built" for its whole life) are redirects now, which is the same guarantee with a true
 * answer at the end of it.
 *
 * A REDIRECT IS HOW A SCREEN RETIRES HERE, and there are now six of them. Three came from
 * rebuild 2's merges (see the Settings block below) and three from the navigation
 * consolidation: `waslo_menus` and `waslo_forms` became TABS — on Tasks and on Approved
 * messages — and the tab is a QUERY PARAMETER, so this table grows by zero records and
 * every count in `assert_waslo_seam.sh` stays put. A record is never simply deleted: the
 * name is in owners' bookmarks and in `nav.js`'s `activeOn` lists, and an unregistered name
 * does not 404, it blanks the whole primary navigation.
 *
 * `config/routes.rb:20`'s `get '/app/*params'` catch-all already serves any path under
 * `/app`, so nothing here needs a Rails route.
 */

import { frontendURL } from '../../../helper/URLHelper';

import { wasloProvisionedGuard } from './provisioning-guard.js';

import WasloRouteView from './WasloRouteView.vue';
import ActivityPage from './pages/ActivityPage.vue';
import AnswersPage from './pages/AnswersPage.vue';
import AnswerSourcePage from './pages/AnswerSourcePage.vue';
import OverviewPage from './pages/OverviewPage.vue';
import PersonalityPage from './pages/PersonalityPage.vue';
import FirstDraftPage from './pages/FirstDraftPage.vue';
import ConnectAiPage from './pages/ConnectAiPage.vue';
import CreditsBuyPage from './pages/CreditsBuyPage.vue';
import CreditsReturnPage from './pages/CreditsReturnPage.vue';
import TasksPage from './pages/TasksPage.vue';
import TaskComposePage from './pages/TaskComposePage.vue';
import FormArrangePage from './pages/FormArrangePage.vue';
import MenuEditorPage from './pages/MenuEditorPage.vue';
import TemplatesPage from './pages/TemplatesPage.vue';
import TemplateEditorPage from './pages/TemplateEditorPage.vue';
import TestPage from './pages/TestPage.vue';
import SettingsRouteView from './pages/settings/SettingsRouteView.vue';
import ConnectionsPage from './pages/settings/ConnectionsPage.vue';
import ConnectionEditorPage from './pages/settings/ConnectionEditorPage.vue';
import OutboundRouteView from './pages/outbound/OutboundRouteView.vue';
import OutboundSetupPage from './pages/outbound/OutboundSetupPage.vue';
import OutboundLogPage from './pages/outbound/OutboundLogPage.vue';
import OutboundMessagePage from './pages/outbound/OutboundMessagePage.vue';
import OutboundNumbersPage from './pages/outbound/OutboundNumbersPage.vue';

// Not `['administrator', 'agent']` like Captain: this section changes what the assistant
// says to real customers and what it is allowed to do to real conversations.
const meta = { permissions: ['administrator'] };

const withTitle = titleKey => ({ ...meta, wasloTitleKey: titleKey });

export const routes = [
  {
    path: frontendURL('accounts/:accountId/waslo'),
    component: WasloRouteView,
    // ONE GUARD FOR THE WHOLE SECTION, on the parent record: `beforeEnter` on a parent
    // runs for every navigation that lands on any child, so a page added below is gated by
    // existing. Hiding the nav rows does not unregister these paths — a bookmark into
    // `/waslo/answers` on a workspace with no assistant would otherwise still land on a
    // screen that can only fail. See `provisioning-guard.js`.
    beforeEnter: wasloProvisionedGuard,
    children: [
      {
        path: frontendURL('accounts/:accountId/waslo'),
        name: 'waslo_overview',
        component: OverviewPage,
        meta: withTitle('WASLO.NAV.OVERVIEW'),
      },
      {
        // Not in the nav, and never built: it rendered `WasloNotReadyPage` — "this is still
        // being built" — for its whole life, which is a worse answer than the page it was
        // going to sit in front of. It is a redirect onto Overview now. The NAME does not
        // move: Overview's `activeOn` claims it (`nav.js`), and `router.resolve()` throws on
        // an unregistered name inside `SidebarGroup`'s computed, which blanks the primary nav
        // rather than 404ing one row.
        path: frontendURL('accounts/:accountId/waslo/setup'),
        name: 'waslo_setup',
        redirect: to => ({ name: 'waslo_overview', params: to.params }),
        meta: withTitle('WASLO.NAV.OVERVIEW'),
      },
      {
        // P-8.4: where the Claude connector's sign-in asks her to Allow. Not in the nav: Claude
        // sends her here with `?request=`, and Overview claims the name so the section stays lit.
        path: frontendURL('accounts/:accountId/waslo/connect-ai'),
        name: 'waslo_connect_ai',
        component: ConnectAiPage,
        meta: withTitle('WASLO.CLAUDE.CONNECT.TITLE'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/credits/buy'),
        name: 'waslo_credits_buy',
        component: CreditsBuyPage,
        meta: withTitle('WASLO.BUY_CREDITS.TITLE'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/credits/return'),
        name: 'waslo_credits_return',
        component: CreditsReturnPage,
        meta: withTitle('WASLO.BUY_CREDITS.RETURN.TITLE'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/answers'),
        name: 'waslo_answers',
        component: AnswersPage,
        meta: withTitle('WASLO.NAV.ANSWERS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/answers/:sourceId'),
        name: 'waslo_answer_source',
        component: AnswerSourcePage,
        meta: withTitle('WASLO.NAV.ANSWERS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/personality'),
        name: 'waslo_personality',
        component: PersonalityPage,
        meta: withTitle('WASLO.NAV.PERSONALITY'),
      },
      {
        // P-8.1 "Write it for me". Its own page rather than a dialog: the draft is long, it is
        // read line by line, and a reload must land on the same paid draft.
        path: frontendURL('accounts/:accountId/waslo/personality/first-draft'),
        name: 'waslo_personality_first_draft',
        component: FirstDraftPage,
        meta: withTitle('WASLO.NAV.PERSONALITY'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/tasks'),
        name: 'waslo_tasks',
        component: TasksPage,
        meta: withTitle('WASLO.NAV.TASKS'),
      },
      {
        // Static segments outrank dynamic ones in vue-router 4's route ranking, so
        // `/tasks/new` cannot be swallowed by `/tasks/:taskId` regardless of order here.
        // That is the fix for the Hono ordering hazard the BFF had to work around.
        path: frontendURL('accounts/:accountId/waslo/tasks/new'),
        name: 'waslo_task_new',
        component: TaskComposePage,
        meta: withTitle('WASLO.NAV.TASKS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/tasks/:taskId'),
        name: 'waslo_task_edit',
        component: TaskComposePage,
        meta: withTitle('WASLO.NAV.TASKS'),
      },
      {
        // P-4.10: arranging a task's WhatsApp form into pages. Under the task, because it edits
        // the task (its definition's `form` block) and saves through the task's own save. One
        // more segment than `waslo_task_edit`, so the two cannot match the same URL.
        path: frontendURL('accounts/:accountId/waslo/tasks/:taskId/form'),
        name: 'waslo_task_form',
        component: FormArrangePage,
        meta: withTitle('WASLO.NAV.TASKS'),
      },
      {
        // ── MENUS IS A TAB ON TASKS, AND THIS RECORD STAYED ──────────────────────────
        //
        // The list moved into `components/MenusTab.vue` and is reached as
        // `…/waslo/tasks?tab=menus`. The tab is a QUERY PARAMETER, not a route: this table
        // therefore grows by zero records, which is what keeps `assert_waslo_seam.sh`'s
        // `dashboard.routes.js` spread assertion — and every other count in that script —
        // exactly where it was.
        //
        // The name is kept for the reason the rebuild-2 names below are: it is in owners'
        // bookmarks, in support replies and in `nav.js`'s `activeOn` for the Tasks row, and
        // `router.resolve()` THROWS on an unregistered name inside a computed during the
        // sidebar's render. A deleted name does not 404; it blanks the whole primary nav.
        //
        // THE QUERY IS BUILT HERE AND NOT COPIED FROM THE INCOMING ONE. `to.query` on a
        // bookmark of this path can carry anything at all, and the only thing this redirect
        // is entitled to say is which tab it meant.
        path: frontendURL('accounts/:accountId/waslo/menus'),
        name: 'waslo_menus',
        redirect: to => ({
          name: 'waslo_tasks',
          params: to.params,
          query: { tab: 'menus' },
        }),
        meta: withTitle('WASLO.NAV.MENUS'),
      },
      {
        // Static beats dynamic in vue-router 4's ranking, so `new` is never read as a menuId.
        path: frontendURL('accounts/:accountId/waslo/menus/new'),
        name: 'waslo_menu_new',
        component: MenuEditorPage,
        meta: withTitle('WASLO.NAV.MENUS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/menus/:menuId'),
        name: 'waslo_menu_edit',
        component: MenuEditorPage,
        meta: withTitle('WASLO.NAV.MENUS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/templates'),
        name: 'waslo_templates',
        component: TemplatesPage,
        meta: withTitle('WASLO.NAV.TEMPLATES'),
      },
      {
        // The number rides in the QUERY (`?inboxId=`), not in the path: templates are approved
        // per WhatsApp number, and a wrong one is a wrong answer rather than a smaller one —
        // but it is a parameter of one screen, not a second screen, so it must not multiply
        // the route table by the number of senders a workspace has.
        path: frontendURL('accounts/:accountId/waslo/templates/new'),
        name: 'waslo_template_new',
        component: TemplateEditorPage,
        meta: withTitle('WASLO.NAV.TEMPLATES'),
      },
      {
        // EDITING ONE META ALREADY HOLDS, which this section used to refuse to offer. The reason
        // given was that the wording lives at Meta and not here — true, and not a reason: Meta
        // publishes `POST /{template_id}`, and without it an owner fixing a typo had to DELETE
        // the template, irreversibly, losing its name for thirty days, and write it again.
        //
        // The id is META'S, in the path, because it names the thing being changed; the number
        // stays in the query beside it for the same reason as `new`. The editor locks the name
        // and the language (Meta accepts neither on that endpoint) and locks the category when
        // the template is approved (Meta refuses a category change on one) — every one of those
        // rules is Meta's, shown where she decides rather than discovered as a refusal.
        //
        // `new` is declared FIRST so nothing rests on how a router resolves a static segment
        // against a dynamic sibling.
        path: frontendURL('accounts/:accountId/waslo/templates/:templateId'),
        name: 'waslo_template_edit',
        component: TemplateEditorPage,
        meta: withTitle('WASLO.NAV.TEMPLATES'),
      },
      {
        // ONE ROUTE, and the absences are deliberate in the same way the templates pair's are.
        //
        // There is no `waslo_form_new` and no editor: a form's contents are DERIVED from a task's
        // steps, so the place to change what it asks is the task editor, not a second authoring
        // screen that could disagree with it. And there is no per-form screen, because a
        // published form's authority is Meta's own record of it — a local page would be a second
        // place for its status to be wrong, which is the exact defect the templates screen
        // exists to avoid.
        //
        // The number rides in the picker on the page rather than in the path: forms are published
        // per WhatsApp number, and a wrong one is a wrong answer rather than a smaller one — but
        // it is a parameter of one screen, not a second screen.
        // AND IT IS NOW A TAB ON APPROVED MESSAGES, for the reason the nav row already gave
        // for sitting beside it: both are one WhatsApp number's inventory at Meta. The screen
        // moved into `components/FormsTab.vue`; this record is a redirect onto
        // `…/waslo/templates?tab=forms`, and the name is kept because `nav.js` names it in the
        // Approved-messages row's `activeOn` and an unregistered name blanks the primary nav.
        path: frontendURL('accounts/:accountId/waslo/forms'),
        name: 'waslo_forms',
        redirect: to => ({
          name: 'waslo_templates',
          params: to.params,
          query: { tab: 'forms' },
        }),
        meta: withTitle('WASLO.NAV.FORMS'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/test'),
        name: 'waslo_test',
        component: TestPage,
        meta: withTitle('WASLO.NAV.TEST'),
      },
      {
        path: frontendURL('accounts/:accountId/waslo/activity'),
        name: 'waslo_activity',
        // The period is a QUERY parameter (`?range=30`), not a path segment: it is a view
        // of one screen rather than a different screen, and it must not multiply the route
        // table by three. The page falls back to 30 days when it is absent or not one of
        // the three the rollup exists for.
        component: ActivityPage,
        meta: withTitle('WASLO.NAV.ACTIVITY'),
      },
      {
        // The second route that never got built. It rendered "this is still being built" for
        // one run of one conversation; Activity itself now opens a transcript in place, so
        // there is nothing behind this path and a redirect onto the list is the true answer.
        // The name stays registered — Activity's own row claims it in `activeOn`.
        path: frontendURL('accounts/:accountId/waslo/activity/:runId'),
        name: 'waslo_activity_run',
        redirect: to => ({
          name: 'waslo_activity',
          params: { accountId: to.params.accountId },
        }),
        meta: withTitle('WASLO.NAV.ACTIVITY'),
      },
      {
        // THE SETTINGS SUB-GROUP IS GONE FROM THE NAV, AND THIS RECORD STAYED.
        //
        // Two of its three children were merged away in rebuild 2 — Channels into
        // Overview's channel list, Permissions into Personality as "What it may do" — and
        // Connections moved under the developer sub-group. What is left is a parent whose
        // only remaining child is Connections, and three NAMES THAT MUST KEEP RESOLVING.
        //
        // Deleting the retired names would have been the tidy-looking mistake. They are in
        // owners' bookmarks and in support replies, and `router.resolve()` THROWS on an
        // unregistered name — so anything still holding one would not 404, it would take
        // the whole primary nav down with it. They are redirects instead: the URL survives,
        // the owner lands on the page that absorbed the screen, and nothing throws.
        //
        // THE REDIRECT IS ON THE PARENT ITSELF, not on a second record of the same path.
        // Two records for one path resolve by insertion order, which is a rule nobody
        // reading this file would know they were relying on; one record cannot be
        // ambiguous. `waslo_settings` now lands on Overview, because Channels — the page
        // it used to land on — is what Overview absorbed.
        path: frontendURL('accounts/:accountId/waslo/settings'),
        name: 'waslo_settings',
        component: SettingsRouteView,
        redirect: to => ({ name: 'waslo_overview', params: to.params }),
        meta,
        children: [
          {
            // Merged into Overview. The name and the path are kept alive deliberately.
            path: frontendURL('accounts/:accountId/waslo/settings/channels'),
            name: 'waslo_settings_channels',
            redirect: to => ({ name: 'waslo_overview', params: to.params }),
            meta: withTitle('WASLO.NAV.OVERVIEW'),
          },
          {
            // Merged into Personality, as the "What it may do" section.
            path: frontendURL('accounts/:accountId/waslo/settings/permissions'),
            name: 'waslo_settings_permissions',
            redirect: to => ({ name: 'waslo_personality', params: to.params }),
            meta: withTitle('WASLO.NAV.PERSONALITY'),
          },
          {
            path: frontendURL('accounts/:accountId/waslo/settings/connections'),
            name: 'waslo_settings_connections',
            component: ConnectionsPage,
            meta: withTitle('WASLO.NAV.CONNECTIONS'),
          },
          {
            // Static segments outrank dynamic ones in vue-router 4's ranking, so `new`
            // cannot be swallowed by `:toolId` regardless of the order here.
            path: frontendURL(
              'accounts/:accountId/waslo/settings/connections/new'
            ),
            name: 'waslo_connection_new',
            component: ConnectionEditorPage,
            meta: withTitle('WASLO.NAV.CONNECTIONS'),
          },
          {
            path: frontendURL(
              'accounts/:accountId/waslo/settings/connections/:toolId'
            ),
            name: 'waslo_connection_edit',
            component: ConnectionEditorPage,
            meta: withTitle('WASLO.NAV.CONNECTIONS'),
          },
        ],
      },
      {
        // THE OUTBOUND API SUB-GROUP, built to the same shape as Settings above and for
        // the same reasons: one parent, absolute child paths, and the redirect ON THE
        // PARENT RECORD rather than a second record for the same path (two records for
        // one path resolve by insertion order, which is a rule nobody reading this file
        // would know they were relying on).
        //
        // ALL FIVE NAMES WERE REGISTERED BEFORE FOUR OF THE PAGES EXISTED. That was not
        // tidiness: `SidebarGroup` resolves every child's `to` through
        // `router.resolve()`, which throws on an unregistered name, inside a computed,
        // during the sidebar's render — so a nav entry pointing at an unbuilt page does
        // not hide one row, it blanks the whole primary nav. The four components below
        // replaced `WasloNotReadyPage` in Wave 3 and NOTHING ELSE MOVED: the names, the
        // paths and the nav entries are byte-identical to what they were, which is the
        // only reason swapping them could not break the sidebar it had been holding up.
        path: frontendURL('accounts/:accountId/waslo/outbound'),
        name: 'waslo_outbound',
        component: OutboundRouteView,
        redirect: to => ({
          name: 'waslo_outbound_setup',
          params: to.params,
        }),
        meta,
        children: [
          {
            path: frontendURL('accounts/:accountId/waslo/outbound/setup'),
            name: 'waslo_outbound_setup',
            component: OutboundSetupPage,
            meta: withTitle('WASLO.NAV.OUTBOUND_SETUP'),
          },
          {
            path: frontendURL('accounts/:accountId/waslo/outbound/log'),
            name: 'waslo_outbound_log',
            component: OutboundLogPage,
            meta: withTitle('WASLO.NAV.OUTBOUND_LOG'),
          },
          {
            // One message. The id is the send's PUBLIC id (a string), not a row id, so
            // an owner can paste the reference her own system recorded straight into the
            // URL bar and land on the message it names.
            path: frontendURL(
              'accounts/:accountId/waslo/outbound/log/:messageId'
            ),
            name: 'waslo_outbound_message',
            component: OutboundMessagePage,
            meta: withTitle('WASLO.NAV.OUTBOUND_LOG'),
          },
          {
            path: frontendURL('accounts/:accountId/waslo/outbound/numbers'),
            name: 'waslo_outbound_numbers',
            component: OutboundNumbersPage,
            meta: withTitle('WASLO.NAV.OUTBOUND_NUMBERS'),
          },
        ],
      },
    ],
  },
];

export default routes;
